import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {ChatHeader} from './ChatHeader';
import {ChatInput} from './ChatInput';
import {ChatTabs} from './ChatTabs';
import {AGENT_CHAT_DRAWER_WIDTH} from './constants';
import {HistoryTab} from './HistoryTab';
import {MessagesTab} from './MessagesTab';
import {ResizeHandle} from './ResizeHandle';
import {useFloatingWindow} from './useFloatingWindow';
import {useWbAgentChat} from './useWbAgentChat';
import {useWbAgentChatContext} from './WbAgentChatContext';

export {AGENT_CHAT_DRAWER_WIDTH} from './constants';
export type {ChatMessage, ProgressStep} from './useWbAgentChat';

const WbAgentChatDrawer: React.FC = () => {
  const {isOpen, close, entity, project} = useWbAgentChatContext();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const el = messagesContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }, []);

  const chat = useWbAgentChat({
    isOpen,
    entity,
    project,
    onScrollRequest: scrollToBottom,
  });

  const floating = useFloatingWindow();

  // ── Sticky offset for sidebar mode ──────────────────────────────────
  const [topOffset, setTopOffset] = useState(0);
  useEffect(() => {
    const el = drawerRef.current;
    if (!el) {
      return;
    }
    const update = () => {
      setTopOffset(Math.max(0, el.getBoundingClientRect().top));
    };
    update();
    window.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update, {passive: true});
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Tailwind>
      <div
        ref={drawerRef}
        className={`flex flex-col bg-[#F8F8F8] ${
          floating.popped
            ? 'fixed z-[1300] overflow-hidden rounded-[12px] border border-[#DFE0E2] shadow-[0_8px_32px_rgba(0,0,0,0.18),0_2px_8px_rgba(0,0,0,0.08)]'
            : 'sticky top-0 self-start border-l border-[#DFE0E2]'
        }`}
        style={
          floating.popped
            ? {
                top: floating.floatPos.y,
                left: floating.floatPos.x,
                width: floating.floatSize.w,
                height: floating.floatSize.h,
              }
            : {
                width: AGENT_CHAT_DRAWER_WIDTH,
                minWidth: AGENT_CHAT_DRAWER_WIDTH,
                height: `calc(100vh - ${topOffset}px)`,
              }
        }>
        <ChatHeader
          available={chat.available}
          popped={floating.popped}
          onDragStart={floating.handleDragStart}
          onPopOut={floating.handlePopOut}
          onDockBack={floating.handleDockBack}
          onClose={close}
        />
        <ChatTabs chat={chat} />

        {chat.activeTab === 'history' && <HistoryTab chat={chat} />}
        {chat.activeTab === 'messages' && (
          <MessagesTab
            chat={chat}
            isOpen={isOpen}
            messagesContainerRef={messagesContainerRef}
          />
        )}

        <ChatInput chat={chat} />

        {floating.popped && (
          <ResizeHandle onMouseDown={floating.handleResizeStart} />
        )}
      </div>
    </Tailwind>
  );
};

export default WbAgentChatDrawer;
